import { Component, OnInit, NgZone, Input, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import anime from "animejs/lib/anime.es.js";
import { BaseComponent, UserService, WindowService, WorkspaceService, HelperService } from "@mypxplat/xplat/core";
import { Router } from "@angular/router";
import { AppService, AvoService, IForegroundMessage, MessagingService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { ReviewConnectionComponent } from "../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "myp-notification-item",
  templateUrl: "notification-item.component.html",
  standalone: false,
})
export class NotificationItemComponent extends BaseComponent implements OnInit {
  @Input() notification: any;
  @Output() deleted: EventEmitter<boolean> = new EventEmitter();
  @Output() close: EventEmitter<void> = new EventEmitter();
  public includeChevron: boolean = true;
  public photo: string;
  public subject: string;
  public description: string;
  public replyingTo: string;
  public targetPostID: string;
  public actions: Array<any>;
  public hasAction: boolean = false;
  public extraMessage: string;
  constructor(
    userService: UserService,
    private _win: WindowService,
    public communityService: WebCommunityService,
    private _zone: NgZone,
    public router: Router,
    public workspaceService: WorkspaceService,
    public avoService: AvoService,
    public translate: TranslateService,
    public appService: AppService,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.notification.type == "collaboration_invite") {
      this.photo = this.notification.payload?.invited_by?.photo;
      if (this.workspaceService.collabsMap[this.notification.payload?.workspace_id]) {
        this.extraMessage = '<span style="color: green; font-weight: bold;">You accepted this invite!</span>';
      } else {
        this.actions = [
          {
            title: "Accept",
            class: "btn btn-sm m-r-5 btn-primary",
            action: () => {
              let args = {
                id: this.notification.payload.invitation_id,
                status: "approved",
              };
              this.workspaceService.updateInvitation(args).subscribe(
                (result) => {
                  this.delete();
                  this.router.navigate(["workspaces/detail", this.notification.payload.workspace_id]);
                  const page = this.avoService.getPageProperties({ pageName: "Workspace Detail" });
                  const te = this.avoService.trackEvent();
                  if (te && typeof te.workspaceViewed === "function") {
                    const extra = {
                      name: "Workspace Detail",
                      workspaceId: parseInt(this.notification.payload.workspace_id, 10),
                      eventType: "invite",
                      interactionType: "accepted",
                    };
                    const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
                    te.workspaceViewed(payload);
                  }
                },
                (error) => {
                  this.appService.alertError(error);
                }
              );
            },
          },
          {
            title: "Reject",
            class: "btn btn-sm m-r-5 btn-blank",
            action: () => {
              let args = {
                id: this.notification.payload.invitation_id,
                status: "rejected",
              };
              this.workspaceService.updateInvitation(args).subscribe(
                (result) => {
                  this.delete();
                  alert(this.translate.instant("sphere.workspaces.invitation_rejected"));
                  const page = this.avoService.getPageProperties({ pageName: "Workspace Detail" });
                  const te = this.avoService.trackEvent();
                  if (te && typeof te.workspaceViewed === "function") {
                    const extra = {
                      name: "Workspace Detail",
                      workspaceId: parseInt(this.notification.payload.workspace_id, 10),
                      eventType: "invite",
                      interactionType: "rejected",
                    };
                    const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
                    te.workspaceViewed(payload);
                  }
                },
                (error) => {
                  this.appService.alertError(error);
                }
              );
            },
          },
        ];
      }
    } else {
      if (!this.notification.url) this.includeChevron = false;
    }
  }

  action() {
    if (this.notification.payload?.primary_post_id) {
      this.go(this.notification.payload?.original_post?.id || this.notification.payload?.primary_post_id);
    } else if (this.notification.type == "collaboration_invite") {
      if (this.workspaceService.collabsMap[this.notification.payload?.workspace_id]) {
        this.router.navigate(["workspaces/detail/" + this.notification.payload?.workspace_id]);
      } else {
        this.router.navigate(["workspaces"]);
      }
    } else if (this.notification.type == "community_connection_invite") {
      this.router.navigate(["profile/" + this.userService.user.id + "/connections"], { queryParams: { reviewConnectionFor: this.notification.payload.requested_by } });
      this.close.next();
    } else if (this.notification.payload?.file_id) {
      this.router.navigate(["workspaces/file/" + this.notification.payload?.file_id]);
    } else if (this.notification.payload?.workspace_id) {
      this.router.navigate(["workspaces/detail/" + this.notification.payload?.workspace_id]);
    } else if (this.notification.type == "community_direct_message") {
      this.router.navigate(["messages/" + this.notification.payload?.author.id]);
    } else if (this.notification.url) {
      window.location = this.notification.url;
    }
  }

  /**
   * Older notifications about a REPLY (a like, reaction or mention on it) were linked to the reply's
   * own id, which opened the reply as though it were a post. Their payload carries the thread as
   * `original_post`, so point the link at that. New ones are linked to the thread already.
   */
  private threadUrl(url: string): string {
    const replyId = this.notification.payload?.primary_post_id;
    const threadId = this.notification.payload?.original_post?.id;
    if (!replyId || !threadId || `${replyId}` === `${threadId}`) return url;
    return url.replace(new RegExp(`(/post/|/betapost/)${replyId}(?=[/?#]|$)`), `$1${threadId}`);
  }

  go(postId) {
    if (this.notification.url && this.notification.url.indexOf("beta")) {
      window.location.href = this.threadUrl(this.notification.url);
    } else {
      this.router.navigate(["community/channels"], {
        queryParams: {
          postid: postId,
        },
      });
      this.close.next();
    }
  }

  delete(args?) {
    if (args) args.stopPropagation();
    this.communityService.deleteNotification(this.notification.id).then(() => {
      this.deleted.next(true);
    });
  }
}
